import type { Component } from 'vue';
import { defineAsyncComponent } from 'vue';
import { 
  NODE_PATHS, NODE_CLASS_NAMES, NODE_COMPONENT_NAMES,
  FormInput, ArrayInput, JsonInput,
  TimerTriggerNode, InitialNode,
  HttpRequest, WebSocketClient, FetchRequest,
  CustomFunction, DebugNode, IfElseNode, SwitchNode, ForEachNode, IsEmptyNode, CloneDeepNode, FilterNode, FindNode,
  VariableGet, VariableSet, ResourceGet
} from 'mflows';

// 使用按需异步加载的方式引入组件，减少首屏体积

// 新增：数据处理节点组件
const IsEmptyComponent = defineAsyncComponent(() => import('@components/nodes/processing/components/IsEmpty.vue'))
const CloneDeepComponent = defineAsyncComponent(() => import('@components/nodes/processing/components/CloneDeep.vue'))
const FilterComponent = defineAsyncComponent(() => import('@components/nodes/processing/components/Filter.vue'))
const FindComponent = defineAsyncComponent(() => import('@components/nodes/processing/components/Find.vue'))

// 使用按需异步加载的方式引入组件，减少首屏体积
const FormInputComponent = defineAsyncComponent(() => import('@components/nodes/inputs/components/FormInput.vue'))
const ArrayInputComponent = defineAsyncComponent(() => import('@components/nodes/inputs/components/ArrayInput.vue'))
const JsonInputComponent = defineAsyncComponent(() => import('@components/nodes/inputs/components/JsonInput.vue'))

const TimerTriggerComponent = defineAsyncComponent(() => import('@components/nodes/triggers/components/TimerTrigger.vue'))
const InitialNodeComponent = defineAsyncComponent(() => import('@components/nodes/triggers/components/InitialNode.vue'))

const CustomFunctionComponent = defineAsyncComponent(() => import('@components/nodes/processing/components/CustomFunction.vue'))
const DebugNodeComponent = defineAsyncComponent(() => import('@components/nodes/processing/components/DebugNode.vue'))
const CustomFunctionDrawerComponent = defineAsyncComponent(() => import('@components/nodes/processing/components/CustomFunctionDrawer.vue'))
const IfElseComponent = defineAsyncComponent(() => import('@components/nodes/processing/components/IfElse.vue'))
const SwitchComponent = defineAsyncComponent(() => import('@components/nodes/processing/components/Switch.vue'))
const ForEachComponent = defineAsyncComponent(() => import('@components/nodes/processing/components/ForEach.vue'))
const HttpRequestComponent = defineAsyncComponent(() => import('@components/nodes/http/httpRequest/component.vue'))
const WebSocketClientComponent = defineAsyncComponent(() => import('@components/nodes/http/webSocketClient/component.vue'))
const FetchRequestComponent = defineAsyncComponent(() => import('@components/nodes/http/fetchRequest/component.vue'))
// 新增：变量读写节点组件
const VariableGetComponent = defineAsyncComponent(() => import('@components/nodes/variables/variableGet/component.vue'))
const VariableSetComponent = defineAsyncComponent(() => import('@components/nodes/variables/variableSet/component.vue'))
// 新增：资源读取节点组件
const ResourceGetComponent = defineAsyncComponent(() => import('@components/nodes/resources/resourceGet/component.vue'))
const IfElsePropertyPanelComponent = defineAsyncComponent(() => import('@components/nodes/processing/components/IfElsePropertyPanel.vue'))
const SwitchPropertyPanelComponent = defineAsyncComponent(() => import('@components/nodes/processing/components/SwitchPropertyPanel.vue'))
const ForEachPropertyPanelComponent = defineAsyncComponent(() => import('@components/nodes/processing/components/ForEachPropertyPanel.vue'))

// 面板引入
// 属性面板：自定义函数使用同一抽屉组件
// 兼容命名：属性面板别名
const CustomFunctionPropertyPanel = CustomFunctionDrawerComponent;
// import formIcon from '@/assets/icon/form.png'
import timerIcon from '@/assets/icon/timer.png'
import initialIcon from '@/assets/icon/initial.png'
import debugIcon from '@/assets/icon/debug.png'
import httpIcon from '@/assets/icon/http.png'
import jsonIcon from '@/assets/icon/object.png'
import arrayIcon from '@/assets/icon/array.png'
import functionIcon from '@/assets/icon/function.png'
// 新增：细分数据类型图标（用于 FormInput 子类型）
import colorIcon from '@/assets/icon/color.png'
import stringIcon from '@/assets/icon/string.png'
import numberIcon from '@/assets/icon/number.png'
import sliderIcon from '@/assets/icon/slider.png'
import booleanIcon from '@/assets/icon/boolean.png'

// 新增：各节点属性面板异步组件
const HttpRequestPropertyPanelComponent = defineAsyncComponent(() => import('@components/nodes/http/httpRequest/HttpRequestPropertyPanel.vue'))
const FetchRequestPropertyPanelComponent = defineAsyncComponent(() => import('@components/nodes/http/fetchRequest/FetchRequestPropertyPanel.vue'))
const WebSocketPropertyPanelComponent = defineAsyncComponent(() => import('@components/nodes/http/webSocketClient/WebSocketClientPropertyPanel.vue'))
const TimerTriggerPropertyPanelComponent = defineAsyncComponent(() => import('@components/nodes/triggers/components/TimerTriggerPropertyPanel.vue'))
const JsonEditorPropertyPanelComponent = defineAsyncComponent(() => import('@components/nodes/inputs/components/JsonEditorPropertyPanel.vue'))
const ArrayInputPropertyPanelComponent = defineAsyncComponent(() => import('@components/nodes/inputs/components/ArrayInputPropertyPanel.vue'))
// 新增：变量读写属性面板
const VariableGetPropertyPanelComponent = defineAsyncComponent(() => import('@components/nodes/variables/variableGet/VariableGetPropertyPanel.vue'))
const VariableSetPropertyPanelComponent = defineAsyncComponent(() => import('@components/nodes/variables/variableSet/VariableSetPropertyPanel.vue'))
// 新增：资源读取属性面板
const ResourceGetPropertyPanelComponent = defineAsyncComponent(() => import('@components/nodes/resources/resourceGet/ResourceGetPropertyPanel.vue'))




// 节点类型定义
export type NodeCategory = 'input' | 'process' | 'output' | 'custom';

// 节点注册配置接口
export interface NodeRegistry<T = any> {
  /** 节点显示名称 */
  name: string;
  /** 节点组件（静态引用） */
  component: Component;
  /** 节点逻辑类 */
  logic: any;
  /** 属性面板组件 */
  propertyPanel?: (() => Promise<Component>) | Component | null;
  /** 节点分类 */
  category?: NodeCategory;
  /** 节点图标 */
  icon?: `/${string}` | string;
  /** 节点描述 */
  description?: string;
  /** 节点是否启用 */
  enabled?: boolean;
  /** 节点类型 */
  componentType?: string;
}

// 节点分类项
export interface NodeCategoryItem {
  label: string;
  component: string;
  componentType?: string;
  nodePath: Component | undefined;
  propertyPanel: (() => Promise<Component>) | Component | null | undefined;
  logicPath: any;
}

// 节点分类组
export interface NodeCategoryGroup {
  code: string;
  label: string;
  color: string;
  items: NodeCategoryItem[];
}

// 分类配置输入（更清晰的声明方式）
export interface CategoryItemInput { type: string; label?: string; componentType?: string }
export interface CategoryConfigEntry { code: string; label: string; color: string; items: (string | CategoryItemInput)[] }

// 默认分类配置（仅含类型与展示信息，具体节点由当前已注册的节点决定）
export const DEFAULT_CATEGORY_CONFIG: CategoryConfigEntry[] = [
  {
    code: 'triggers',
    label: '触发器',
    color: '#FF6D00',
    items: [
      { type: 'InitialNode', label: '初始' },
      { type: 'TimerTrigger', label: '定时器' },
    ],
  },
  {
    code: 'data_constants',
    label: '数据常量',
    color: '#7C3AED',
    items: [
      { type: 'FormInput', label: '字符串', componentType: 'text' },
      { type: 'FormInput', label: '数字', componentType: 'number' },
      { type: 'FormInput', label: '布尔值', componentType: 'boolean' },
      { type: 'ArrayInput', label: '数组' },
      { type: 'JsonEditor', label: '对象' },
      // 业务数据：按需注册，未注册自动隐藏
      { type: 'ResourceGet', label: '资源读取' },
      { type: 'VariableGet', label: '变量读取' },
      { type: 'VariableSet', label: '变量设置' },
    ],
  },
  {
    code: 'method_scripts',
    label: '方法脚本',
    color: '#8B5CF6',
    items: [
      { type: 'CustomFunction', label: '自定义函数' },
    ],
  },
  // 新增：数据处理分类
  {
    code: 'data_processing',
    label: '数据处理',
    color: '#10B981',
    items: [
      { type: 'IsEmpty', label: '是否为空' },
      { type: 'CloneDeep', label: '深拷贝' },
      { type: 'Filter', label: '过滤' },
      { type: 'Find', label: '查找' },
    ],
  },
  {
    code: 'control',
    label: '逻辑控制',
    color: '#F59E0B',
    items: [
      { type: 'IfElse', label: '条件分支' },
      { type: 'Switch', label: '多路分支' },
      { type: 'ForEach', label: '遍历' },
    ],
  },
  {
    code: 'network',
    label: '网络',
    color: '#0EA5E9',
    items: [
      { type: 'HttpRequest', label: 'HTTP 请求' },
      { type: 'FetchRequest', label: 'Fetch' },
      { type: 'WebSocket', label: 'WebSocket' },
    ],
  },
  {
    code: 'debug',
    label: '调试器',
    color: '#334155',
    items: [
      { type: 'DebugNode', label: '调试' },
    ],
  },
];

/**
 * 节点管理器类
 * 负责节点的注册、获取、分类和管理
 */
export class NodeManager {
  private static instance: NodeManager;
  private nodes: Record<string, NodeRegistry> = {};
  private categories: Map<string, NodeCategoryGroup> = new Map();
  private debug: boolean = false;
  // 新增：保存最近一次的分类配置，便于后续 rebuild
  private categoryConfig: CategoryConfigEntry[] = [];

  /**
   * 私有构造函数，初始化内置节点
   */
  private constructor() {
    // 不在构造函数中进行默认节点与分类的注册，交由外部初始化
    // this.initDefaultNodes();
    // this.initNodeCategories();
  }

  /**
   * 获取单例实例
   */
  public static getInstance(): NodeManager {
    if (!NodeManager.instance) {
      NodeManager.instance = new NodeManager();
    }
    return NodeManager.instance;
  }

  /**
   * 由外部显式调用，初始化默认节点与分类
   */
  public initializeDefaults(): void {
    this.initDefaultNodes();
    this.initNodeCategories();
  }

  /**
   * 初始化默认节点
   */
  public initDefaultNodes(): void {
    // 输入类节点
    this.register('FormInput', {
      name: '表单输入',
      component: FormInputComponent,
      logic: FormInput,
      propertyPanel: null,
      category: 'input',
      icon: '',
      enabled: true,
      description: '提供表单输入功能',
    });

    this.register('TimerTrigger', {
      name: '定时触发器',
      component: TimerTriggerComponent,
      logic: TimerTriggerNode,
      propertyPanel: TimerTriggerPropertyPanelComponent,
      category: 'input',
      icon: timerIcon,
      enabled: true,
      description: '按设定时间间隔触发流程',
    });

    this.register('InitialNode', {
      name: '初始节点',
      component: InitialNodeComponent,
      logic: InitialNode,
      propertyPanel: null,
      category: 'input',
      icon: initialIcon,
      enabled: true,
      description: '流程的起始节点',
    });

    // 处理类节点
    this.register('CustomFunction', {
      name: '自定义方法',
      component: CustomFunctionComponent,
      logic: CustomFunction,
      propertyPanel: CustomFunctionPropertyPanel,
      category: 'process',
      icon: functionIcon,
      enabled: true,
      description: '执行自定义JavaScript代码',
    });

    this.register('IfElse', {
      name: '条件分支',
      component: IfElseComponent,
      logic: IfElseNode,
      propertyPanel: IfElsePropertyPanelComponent,
      category: 'process',
      icon: functionIcon,
      enabled: true,
      description: '依据条件真值触发分支',
    });

    this.register('Switch', {
      name: '多路分支',
      component: SwitchComponent,
      logic: SwitchNode,
      propertyPanel: SwitchPropertyPanelComponent,
      category: 'process',
      icon: functionIcon,
      enabled: true,
      description: '按 Key 匹配 cases 触发分支',
    });

    this.register('ForEach', {
      name: '遍历',
      component: ForEachComponent,
      logic: ForEachNode,
      propertyPanel: ForEachPropertyPanelComponent,
      category: 'process',
      icon: functionIcon,
      enabled: true,
      description: '对列表逐项触发并在结束时发出完成',
    });

    // 新增：数据处理节点注册
    this.register('IsEmpty', {
      name: '是否为空',
      component: IsEmptyComponent,
      logic: IsEmptyNode,
      propertyPanel: null,
      category: 'process',
      icon: functionIcon,
      enabled: true,
      description: '判断输入是否为空 (es-toolkit.isEmpty)',
    });

    this.register('CloneDeep', {
      name: '深拷贝',
      component: CloneDeepComponent,
      logic: CloneDeepNode,
      propertyPanel: null,
      category: 'process',
      icon: functionIcon,
      enabled: true,
      description: '对输入执行深度拷贝 (es-toolkit.cloneDeep)',
    });

    this.register('Filter', {
      name: '过滤',
      component: FilterComponent,
      logic: FilterNode,
      propertyPanel: null,
      category: 'process',
      icon: functionIcon,
      enabled: true,
      description: '按键与值过滤数组 (es-toolkit.filter)',
    });

    this.register('Find', {
      name: '查找',
      component: FindComponent,
      logic: FindNode,
      propertyPanel: null,
      category: 'process',
      icon: functionIcon,
      enabled: true,
      description: '按键与值查找首项 (es-toolkit.find)',
    });

    this.register('HttpRequest', {
      name: 'HTTP请求',
      component: HttpRequestComponent,
      logic: HttpRequest,
      propertyPanel: HttpRequestPropertyPanelComponent,
      category: 'process',
      icon: httpIcon,
      enabled: true,
      description: '发送HTTP请求并处理响应',
    });

    this.register('FetchRequest', {
      name: 'Fetch请求',
      component: FetchRequestComponent,
      logic: FetchRequest,
      propertyPanel: FetchRequestPropertyPanelComponent,
      category: 'process',
      icon: httpIcon,
      enabled: true,
      description: '使用 fetch API 发起请求',
    });

    this.register('WebSocket', {
      name: 'WebSocket',
      component: WebSocketClientComponent,
      logic: WebSocketClient,
      propertyPanel: WebSocketPropertyPanelComponent,
      category: 'process',
      icon: httpIcon,
      enabled: true,
      description: '建立 WebSocket 连接并处理消息',
    });

    // 默认启用：资源读取
    this.register('ResourceGet', {
      name: '资源读取',
      component: ResourceGetComponent,
      logic: ResourceGet,
      propertyPanel: ResourceGetPropertyPanelComponent,
      category: 'input',
      icon: jsonIcon,
      enabled: true,
      description: '从资源库选择并读取资源',
    });

    // 输出类节点
    this.register('DebugNode', {
      name: '调试节点',
      component: DebugNodeComponent,
      logic: DebugNode,
      propertyPanel: null,
      category: 'output',
      icon: debugIcon,
      enabled: true,
      description: '输出调试信息',
    });

    // 特殊输入节点
    this.register('JsonEditor', {
      name: 'JSON编辑器',
      component: JsonInputComponent,
      logic: JsonInput,
      propertyPanel: JsonEditorPropertyPanelComponent,
      category: 'input',
      icon: jsonIcon,
      enabled: true,
      description: '编辑JSON对象',
    });

    this.register('ArrayInput', {
      name: '数组编辑器',
      component: ArrayInputComponent,
      logic: ArrayInput,
      propertyPanel: ArrayInputPropertyPanelComponent,
      category: 'input',
      icon: arrayIcon,
      enabled: true,
      description: '编辑数组数据',
    });

    // 新增：变量读写节点（默认启用）
    this.register('VariableGet', {
      name: '变量读取',
      component: VariableGetComponent,
      logic: VariableGet,
      propertyPanel: VariableGetPropertyPanelComponent,
      category: 'input',
      icon: jsonIcon,
      enabled: true,
      description: '从上下文读取变量值',
    });

    this.register('VariableSet', {
      name: '变量设置',
      component: VariableSetComponent,
      logic: VariableSet,
      propertyPanel: VariableSetPropertyPanelComponent,
      category: 'input',
      icon: jsonIcon,
      enabled: true,
      description: '将值写入到上下文变量',
    });
  }

  /**
   * 初始化节点分类（改为基于配置构建）
   */
  public initNodeCategories(): void {
    this.setCategories(DEFAULT_CATEGORY_CONFIG);
  }

  /**
   * 设置分类配置并立即重建分类
   */
  public setCategories(config: CategoryConfigEntry[]): void {
    this.categoryConfig = config.slice();
    this.rebuildCategories();
  }

  /**
   * 根据当前注册的节点与最近的配置重建分类
   * - 仅包含已注册且启用的节点
   * - 支持自定义 label/componentType
   */
  public rebuildCategories(): void {
    const map = new Map<string, NodeCategoryGroup>();

    for (const entry of this.categoryConfig) {
      const items: NodeCategoryItem[] = [];

      for (const i of entry.items) {
        const type = typeof i === 'string' ? i : i.type;
        const customLabel = typeof i === 'string' ? undefined : i.label;
        const componentType = typeof i === 'string' ? undefined : i.componentType;

        const registry = this.nodes[type];
        if (!registry || registry.enabled === false) {
          continue; // 未注册或禁用则跳过
        }

        const label = customLabel ?? registry.name ?? type;
        items.push(this.createCategoryItem(label, type, componentType));
      }

      map.set(entry.code, {
        code: entry.code,
        label: entry.label,
        color: entry.color,
        items,
      });
    }

    this.categories = map;
  }

  /**
   * 创建一个分类项
   * @param label 显示标签
   * @param component 组件类型
   * @param componentType 组件子类型（可选）
   * @returns 节点分类项
   */
  private createCategoryItem(label: string, component: string, componentType?: string): NodeCategoryItem {
    return {
      label,
      component,
      componentType,
      nodePath: this.getComponent(component),
      propertyPanel: this.getPropertyPanel(component),
      logicPath: this.getLogicClass(component),
    };
  }

  // ================= 节点注册相关方法 =================

  /**
   * 注册新节点
   * @param type 节点类型
   * @param config 节点配置
   * @returns 是否注册成功
   */
  public register(type: string, config: NodeRegistry): boolean {
    if (this.nodes[type] && this.debug) {
      console.warn(`[NodeManager] 节点类型"${type}"已存在，将被覆盖`);
    }
    
    // 设置默认值
    config.enabled = config.enabled ?? true;
    
    this.nodes[type] = config;
    
    if (this.debug) {
      // 成功注册节点: ${type}
      console.log(`[NodeManager] 成功注册节点: ${type}`);
    }
    
    return true;
  }

  /**
   * 注册多个节点
   * @param nodes 节点配置映射
   * @returns 是否全部注册成功
   */
  public registerMultiple(nodes: Record<string, NodeRegistry>): boolean {
    let allSuccess = true;
    
    for (const [type, config] of Object.entries(nodes)) {
      const success = this.register(type, config);
      if (!success) allSuccess = false;
    }
    
    return allSuccess;
  }

  /**
   * 批量更新节点配置
   * @param updates 节点更新配置映射
   * @returns 是否全部更新成功
   */
  public updateNodes(updates: Record<string, Partial<NodeRegistry>>): boolean {
    let allSuccess = true;
    
    for (const [type, config] of Object.entries(updates)) {
      if (!this.nodes[type]) {
        if (this.debug) {
          console.error(`[NodeManager] 尝试更新不存在的节点: ${type}`);
        }
        allSuccess = false;
        continue;
      }
      
      this.nodes[type] = { ...this.nodes[type], ...config };
      
      if (this.debug) {
        // 成功更新节点: ${type}
        console.log(`[NodeManager] 成功更新节点: ${type}`);
      }
    }
    
    return allSuccess;
  }

  /**
   * 获取节点配置
   * @param type 节点类型
   * @returns 节点配置或undefined
   */
  public getNodeConfig(type: string): NodeRegistry | undefined {
    return this.nodes[type];
  }

  /**
   * 获取所有已注册节点
   * @returns 节点配置映射
   */
  public getAllNodes(): Record<string, NodeRegistry> {
    return { ...this.nodes };
  }

  /**
   * 获取指定分类的节点
   * @param category 节点分类
   * @returns 节点类型列表
   */
  public getNodesByCategory(category: NodeCategory): Record<string, NodeRegistry> {
    const result: Record<string, NodeRegistry> = {};
    
    Object.entries(this.nodes).forEach(([type, config]) => {
      if (config.category === category && config.enabled) {
        result[type] = config;
      }
    });
    
    return result;
  }

  /**
   * 获取已启用的节点
   * @returns 已启用的节点配置映射
   */
  public getEnabledNodes(): Record<string, NodeRegistry> {
    const result: Record<string, NodeRegistry> = {};
    
    Object.entries(this.nodes).forEach(([type, config]) => {
      if (config.enabled) {
        result[type] = config;
      }
    });
    
    return result;
  }

  /**
   * 启用或禁用指定节点
   * @param type 节点类型
   * @param enabled 是否启用
   * @returns 操作是否成功
   */
  public setNodeEnabled(type: string, enabled: boolean): boolean {
    if (!this.nodes[type]) {
      if (this.debug) {
        console.error(`[NodeManager] 节点未注册: ${type}`);
      }
      return false;
    }
    
    this.nodes[type].enabled = enabled;
    return true;
  }

  /**
   * 批量启用或禁用节点
   * @param types 节点类型数组
   * @param enabled 是否启用
   * @returns 是否全部操作成功
   */
  public setNodesEnabled(types: string[], enabled: boolean): boolean {
    let allSuccess = true;
    
    for (const type of types) {
      const success = this.setNodeEnabled(type, enabled);
      if (!success) allSuccess = false;
    }
    
    return allSuccess;
  }

  /**
   * 移除节点注册
   * @param type 节点类型
   * @returns 是否移除成功
   */
  public unregister(type: string): boolean {
    if (!this.nodes[type]) {
      if (this.debug) {
        console.warn(`[NodeManager] 尝试移除不存在的节点: ${type}`);
      }
      return false;
    }
    
    delete this.nodes[type];
    
    if (this.debug) {
      // 成功移除节点: ${type}
      console.log(`[NodeManager] 成功移除节点: ${type}`);
    }
    
    return true;
  }

  /**
   * 批量移除节点注册
   * @param types 节点类型数组
   * @returns 是否全部移除成功
   */
  public unregisterMultiple(types: string[]): boolean {
    let allSuccess = true;
    
    for (const type of types) {
      const success = this.unregister(type);
      if (!success) allSuccess = false;
    }
    
    return allSuccess;
  }

  /**
   * 设置调试模式
   * @param enabled 是否启用调试
   */
  public setDebug(enabled: boolean): void {
    this.debug = enabled;
  }

  // ================= 节点资源获取方法 =================

  /**
   * 使用 NODE_PATHS 动态加载节点逻辑类
   * @param category 节点分类
   * @param nodeName 节点名称  
   * @returns Promise<节点逻辑类>
   */
  public async loadNodeLogicDynamic(category: keyof typeof NODE_PATHS, nodeName: string): Promise<any> {
    try {
      const categoryPaths = NODE_PATHS[category] as Record<string, string>;
      const nodePath = categoryPaths?.[nodeName];
      if (!nodePath) {
        throw new Error(`未找到节点路径: ${category}.${nodeName}`);
      }
      
      // 获取节点类名
      const classNames = NODE_CLASS_NAMES[category as keyof typeof NODE_CLASS_NAMES] as Record<string, string>;
      const className = classNames?.[nodeName];
      if (!className) {
        throw new Error(`未找到节点类名: ${category}.${nodeName}`);
      }
      
      // 构建完整的导入路径
      const fullPath = `@components/nodes/${nodePath.slice(2)}`;
      const module = await import(/* @vite-ignore */ fullPath);
      
      // 返回指定的类导出
      return module[className];
    } catch (error: any) {
      if (this.debug) {
        console.error(`[NodeManager] 动态加载逻辑类失败: ${error.message}`);
      }
      return null;
    }
  }

  /**
   * 使用 NODE_PATHS 动态加载节点组件
   * @param category 节点分类
   * @param nodeName 节点名称
   * @returns Promise<节点组件>  
   */
  public async loadNodeComponentDynamic(category: keyof typeof NODE_PATHS, nodeName: string): Promise<Component | undefined> {
    try {
      const categoryPaths = NODE_PATHS[category] as Record<string, string>;
      const nodePath = categoryPaths?.[nodeName];
      if (!nodePath) {
        throw new Error(`未找到节点路径: ${category}.${nodeName}`);
      }
      
      // 获取节点组件名
      const componentNames = NODE_COMPONENT_NAMES[category as keyof typeof NODE_COMPONENT_NAMES] as Record<string, string>;
      const componentName = componentNames?.[nodeName];
      if (!componentName) {
        throw new Error(`未找到节点组件名: ${category}.${nodeName}`);
      }
      
      // 构建完整的组件路径
      const fullPath = `@components/nodes/${nodePath.slice(2)}`;
      const module = await import(/* @vite-ignore */ fullPath);
      
      // 返回指定的组件导出
      return module[componentName];
    } catch (error: any) {
      if (this.debug) {
        console.error(`[NodeManager] 动态加载组件失败: ${error.message}`);
      }
      return undefined;
    }
  }

  /**
   * 获取节点组件
   * @param type 节点类型
   * @returns 节点组件或undefined
   */
  public getComponent<T extends Component>(type: string): T | undefined {
    try {
      const config = this.nodes[type];
      if (!config?.component) {
        throw new Error(`组件未注册: ${type}`);
      }
      return config.component as T;
    } catch (error: any) {
      if (this.debug) {
        console.error(`[NodeManager] 组件加载失败: ${error.message}`);
      }
      return undefined;
    }
  }

  /**
   * 获取节点逻辑类
   * @param type 节点类型
   * @returns 节点逻辑类或null
   */
  public getLogicClass(type: string): any {
    try {
      const config = this.nodes[type];
      if (!config?.logic) {
        throw new Error(`逻辑类未注册: ${type}`);
      }
      return config.logic;
    } catch (error: any) {
      if (this.debug) {
        console.error(`[NodeManager] 逻辑类加载失败: ${error.message}`);
      }
      return null;
    }
  }

  /**
   * 获取节点图标
   * @param type 节点类型
   * @returns 节点图标或undefined
   */
  public getIcon(type: string, componentType?: string): `/${string}` | string | undefined {
    // 针对 FormInput，根据 componentType 返回相应数据类型图标
    if (type === 'FormInput' && componentType) {
      switch (componentType) {
        case 'color':
          return colorIcon
        case 'text':
          return stringIcon
        case 'number':
          return numberIcon
        case 'slider':
          return sliderIcon
        case 'boolean':
          return booleanIcon
      }
    }
    return this.nodes[type]?.icon;
  }

  /**
   * 获取节点属性面板
   * @param type 节点类型
   * @returns 节点属性面板或undefined
   */
  public getPropertyPanel(type: string): (() => Promise<Component>) | Component | null | undefined {
    return this.nodes[type]?.propertyPanel;
  }

  // ================= 节点分类相关方法 =================

  /**
   * 获取所有节点分类
   * @returns 节点分类Map
   */
  public getNodeCategories(): Map<string, NodeCategoryGroup> {
    return this.categories;
  }

  /**
   * 获取节点分类为数组形式
   * @returns 分类数组
   */
  public getNodeCategoriesAsArray(): NodeCategoryGroup[] {
    return Array.from(this.categories.values());
  }

  /**
   * 获取特定节点分类
   * @param categoryCode 分类代码
   * @returns 分类组或undefined
   */
  public getNodeCategory(categoryCode: string): NodeCategoryGroup | undefined {
    return this.categories.get(categoryCode);
  }

  /**
   * 添加新的节点分类
   * @param categoryCode 分类代码
   * @param category 分类配置
   * @returns 是否添加成功
   */
  public addNodeCategory(categoryCode: string, category: NodeCategoryGroup): boolean {
    if (this.categories.has(categoryCode)) {
      if (this.debug) {
        console.warn(`[NodeManager] 分类代码"${categoryCode}"已存在，将被覆盖`);
      }
    }
    
    this.categories.set(categoryCode, category);
    
    if (this.debug) {
      // 成功添加分类: ${categoryCode}
      console.log(`[NodeManager] 成功添加分类: ${categoryCode}`);
    }

    return true;
  }

  /**
   * 重置所有节点和分类
   * 慎用此方法，将清空所有已注册的节点和分类
   */
  public reset(): void {
    this.nodes = {};
    this.categories.clear();
    this.categoryConfig = [];
    
    if (this.debug) {
      console.warn('[NodeManager] 已重置所有节点和分类');
    }
  }
}

// 创建导出单例
const nodeManager = NodeManager.getInstance();

// 默认导出节点管理器单例
export default nodeManager;
